import { action as mobxAction, runInAction, toJS } from 'mobx';
import { getFixedT } from 'i18next';
import { NAMESPACES } from '~/i18n/namespaces';
import { PartialDeep } from 'type-fest';
import { ToastType } from '@honeybook/hbui/dist/components/HBToast/HBToast.types';
import { IBaseFlowContext, TBlock } from '~/types/builder.types';
import {
    IFlow,
    IFlowTheme,
    IVendorEmail,
    RevisionType,
    IFlowPermission
} from '~/types/model.types';
import {
    FlowApplyActionsErrors,
    IFlowUpdatedResponse,
    IPaymentVariablePendingMarkAsPaid,
    IPaymentVariablePendingMarkAsRefunded,
    IRecurringInvoiceResponse,
    IRecurringScheduleBlockResponse,
    IRequestPayment,
    ITransformedFlowResponse,
    ITransformedPageResponse,
    IVariableResponse,
    PublishFlowReason,
    TBlockResponse,
    TFlowSource,
    TUpdateFlowConfiguration
} from '~/types/server.types';
import {
    encodeHtmlOnApplyActionCommandContent,
    mapActionIdToAction
} from '~/services/actions-manager/actions.utils';
import { Action, ServerActionType } from '~/services/actions-manager/action';
import { ActionName } from '~/services/actions-manager/action-name';
import { Flow } from '~/models/flow.model';
import {
    calculateFlowBaseDataForServer,
    createUpdateFlowCommand,
    extractOldData
} from '~/utils/commands.utils';
import { ContractSidebarScreen } from '~/components/Builder/BuilderSidebar/builder-sidebar.types';
import { FlowConfigurationModel } from '~/models/flow-configuration.model';
import {
    ActionManagerLastActionNumberReason,
    FetchFlowReasons,
    LogEvents
} from '~/services/fullstory/log-events';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { getSubjectForSendTestEmail } from '~/utils/emails.utils';
import { TTrackFunction } from '~/common/useAnalytics';
import { trackError } from '~/services/logger/error.utils';
import colors from '@honeybook/hbui/dist/styles/colors';
import {
    FlowRouteType,
    getExpirationPreviewUrl,
    transformFlowSocketUpdateData
} from '~/utils/flow.utils';
import {
    FlowInformActionTypes,
    IReloadAction
} from '~/common/flow-socket.types';
import {
    CoreOutboundEvents,
    GeneralOutboundEvents
} from '~/components/Builder/Mobile/builderMobileApi.enums';
import { IFlowService } from '~/types/angular.types';
import { isNetworkError } from '~/utils/network.utils';
import { ServerErrorType } from '~/utils/server-response.utils';
import { max, min } from 'lodash';
import { toBinaryBase64 } from '~/utils/text.utils';
import { FlowRevision } from '~/models/flow-revision.model';
import type { RootStore } from '~/stores/root.store';
import { boundMethod } from 'autobind-decorator';
import { IOneComposerEmail } from '~/components/OneComposer/OneComposer.types';
import { SignatureBlock } from '~/models/blocks/signature-block.model';
import {
    ClientPendingTaskType,
    IDeliveryFail,
    IShareFlowTaskData
} from '~/common/hooks/ClientPendingTask/client-pending-task.types';
import { addParamsToUrl } from '~/utils/url.utils';
import { CreateTemplateType } from '~/components/Templates/types/templates.types';
import { convertSmartFieldsToServerFormat } from '~/common/SmartFields/smart-fields.utils';
import { goToFlow } from '~/components/Builder/Common/useFlowNavigation';
import { isReactFirst } from '~/utils/environment.utils';
import { useHistory } from 'react-router-dom';

interface IUpdateFlowContext extends IBaseFlowContext {
    data?: PartialDeep<Flow>;
    oldData?: PartialDeep<Flow>;
}

export interface IFetchFlowOptions {
    preview?: boolean;
    active?: boolean;
    forceMerge?: boolean;
    reason?: FetchFlowReasons;
    regainControlFingerprint?: string;
}

interface ISelectBlockOptions {
    blockId?: string;
    displayRootParentBlock?: string;
    isMultiStageAction?: boolean;
}

export interface ISendReminderProps {
    flow: Flow;
    paymentId: string;
    showSuccessToast?: boolean;
}

type IApplyActionsProps = {
    flow: Flow;
    actions: Array<Action>;
    retryNumber: number;
};
export class FlowController {
    rootStore: RootStore;
    locationBeforePreview = '';
    leadFormT = getFixedT(null, NAMESPACES.LEAD_FORMS, 'errors');

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    async setFlowPermission(flow: Flow, updatedPermissions: IFlowPermission[]) {
        const { flowService } = this.rootStore;
        return flowService.setFlowPermissions(flow._id, updatedPermissions);
    }

    async getFlowPermission(flowId: string) {
        const { flowService } = this.rootStore;
        return flowService.getFlowPermissions(flowId);
    }

    async markAsPaid(
        markAsPaidProps: IPaymentVariablePendingMarkAsPaid,
        flow: Flow,
        paymentId: string
    ) {
        const { flowService } = this.rootStore;
        try {
            const response = await flowService.markAsPaid(
                markAsPaidProps,
                flow._id,
                paymentId
            );

            if (response?.data?.error_type === 'InvalidPaymentRequestError') {
                return this.showMarkAsPaidError(response?.data?.error_message);
            }

            this.syncFlowRevisionsAfterActionOnActiveRevision(response, flow);
            flow.setStatus(response.status);
            flow.setEligibleForMarkAsCompleted(
                response.is_eligible_for_mark_as_completed
            );
            return response;
        } catch (error) {
            return this.showMarkAsPaidError(error?.data?.error_message);
        }
    }

    async requestPayment(params: IRequestPayment) {
        try {
            const { flowService, flowStore } = this.rootStore;
            const response = await flowService.requestPayment(params);
            const flow = flowStore.flows[params.flowId];

            this.syncFlowRevisionsAfterActionOnActiveRevision(response, flow);
        } catch (error) {
            this.rootStore.toastStore.add({
                text: error.data.error_message,
                type: ToastType.Error
            });
        }
    }

    async undoMarkAsPaid(flow: Flow, paymentId: string) {
        const { flowService } = this.rootStore;
        const response = await flowService.undoMarkAsPaid(flow._id, paymentId);
        this.syncFlowRevisionsAfterActionOnActiveRevision(response, flow);
        flow.setStatus(response.status);
        flow.setEligibleForMarkAsCompleted(
            response.is_eligible_for_mark_as_completed
        );
        return response;
    }

    async markAsRefund(
        refundProps: IPaymentVariablePendingMarkAsRefunded,
        flow: Flow,
        paymentId: string
    ) {
        const { flowService } = this.rootStore;
        const response = await flowService.markAsRefund(
            refundProps,
            flow._id,
            paymentId
        );
        this.syncFlowRevisionsAfterActionOnActiveRevision(response, flow);

        return response;
    }

    @boundMethod
    async sendReminder({
        flow,
        paymentId,
        showSuccessToast = false
    }: ISendReminderProps) {
        const { flowService } = this.rootStore;
        try {
            const response = await flowService.sendReminder(
                flow._id,
                paymentId
            );
            this.syncFlowRevisionsAfterActionOnActiveRevision(response, flow);

            if (showSuccessToast) {
                this.rootStore.toastStore.add({
                    text: 'Reminder sent',
                    type: ToastType.Success
                });
            }
        } catch (error) {
            this.rootStore.toastStore.add({
                text: error.data.error_message,
                type: ToastType.Error
            });
        }
    }

    async stopRecurringInvoice(flowId: string, data) {
        const {
            builderUiStore: { selectedFlow },
            flowService,
            flowStore
        } = this.rootStore;
        const response = await flowService.stopRecurringInvoice(flowId, {
            email: data
        });
        flowStore.syncFlowWithServerResponse(
            selectedFlow,
            response.pages,
            response.blocks,
            response.variables,
            {
                revisionType: RevisionType.Active
            }
        );
        flowStore.syncFlowWithServerResponse(
            selectedFlow,
            response.pages,
            response.blocks,
            response.variables,
            {
                revisionType: RevisionType.Draft
            }
        );

        selectedFlow.setEligibleForMarkAsCompleted(
            response.is_eligible_for_mark_as_completed
        );
        selectedFlow.setStatus(response.status);
        return response;
    }

    async getPaymentConnectedInvoice(
        flowId: string,
        paymentBlockId: string
    ): Promise<IRecurringInvoiceResponse> {
        const { flowService } = this.rootStore;
        const resp = await flowService.getPaymentConnectedInvoice(
            flowId,
            paymentBlockId
        );
        const recurringScheduleBlock = resp.blocks.find(
            block => block.type === 'recurring_schedule_block'
        ) as IRecurringScheduleBlockResponse;
        recurringScheduleBlock.current_payment_block_id = paymentBlockId;
        return resp;
    }

    async getInvoiceByPaymentId(
        flowId: string,
        paymentId: string,
        isRecurring: boolean
    ): Promise<IRecurringInvoiceResponse> {
        const { flowService } = this.rootStore;
        const resp = await flowService.getInvoiceByPaymentId(flowId, paymentId);
        if (isRecurring) {
            const recurringScheduleBlock = resp.blocks.find(
                block => block.type === 'recurring_schedule_block'
            ) as IRecurringScheduleBlockResponse;
            recurringScheduleBlock.current_payment_block_id =
                resp.payment_block_id;
        }
        return resp;
    }

    async fetchPlainFlow(
        flowId: string,
        { preview, active, regainControlFingerprint }: IFetchFlowOptions = {}
    ) {
        const { fullstoryService, flowService, eventEmitterCtrl } =
            this.rootStore;

        try {
            return await flowService.fetchFlow(flowId, {
                preview,
                active,
                regainControlFingerprint
            });
        } catch (error) {
            fullstoryService.error(error?.data?.error_message || error);
            if (eventEmitterCtrl?.isMobile) {
                eventEmitterCtrl.trigger(GeneralOutboundEvents.ServerError, {
                    type: ServerErrorType.FlowLoadedError,
                    message: 'Error loading the smart file',
                    reason: error?.data?.error_message
                });
            } else {
                window.location.href = '/app/home/';
            }
            this.rootStore.toastStore.add({
                text: error.data.error_message,
                type: ToastType.Error
            });

            return Promise.reject();
        }
    }

    async fetchFlow(
        flowId: string,
        {
            preview,
            active,
            forceMerge,
            reason,
            regainControlFingerprint
        }: IFetchFlowOptions = {}
    ) {
        const { fullstoryService, actionsManager, networkStore } =
            this.rootStore;

        if (!networkStore.isOnline) {
            fullstoryService.info(LogEvents.FetchFlowOfflineDismiss);

            return Promise.resolve();
        }

        const actionNumberBeforeFetch = actionsManager.lastActionNumber;

        fullstoryService.info(LogEvents.BeforeFlowFetch(flowId, reason));
        const response = await this.fetchPlainFlow(flowId, {
            preview,
            active,
            regainControlFingerprint
        });

        this.mergeFlowResponse(
            response,
            actionNumberBeforeFetch,
            active,
            forceMerge
        );

        return Promise.resolve();
    }
    async fetchFlowAndLoadToStore(flowId: string) {
        const { flowService, flowStore } = this.rootStore;

        const response = await flowService.fetchFlow(flowId);

        flowStore.loadTransformedFlowResponse(response);
    }

    async getFlowLastActionNumber(flowId: string) {
        const { fullstoryService, flowService } = this.rootStore;
        try {
            const { last_action_number } =
                await flowService.getFlowLastActionNumber(flowId);

            return last_action_number;
        } catch (error) {
            if (isNetworkError(error)) {
                fullstoryService.error(
                    'Failed to get flow last action number because of network error'
                );

                return Promise.reject(error);
            }

            fullstoryService.error(error.data?.error_message);

            return -1;
        }
    }

    private loadTransformedFlowResponse(
        response: ITransformedFlowResponse,
        active = false
    ) {
        const { eventEmitterCtrl, fullstoryService } = this.rootStore;

        this.updateFlowInStore(
            response.flow,
            response.pages,
            response.blocks,
            response.variables,
            !active
        );

        fullstoryService.info(LogEvents.FlowLoaded(response.flow._id));

        eventEmitterCtrl.trigger(CoreOutboundEvents.PageLoaded, {});
    }

    async fetchActiveFlow(flowId: string, asBothDraftAndActive = false) {
        const { flowStore } = this.rootStore;
        const response = await this.fetchPlainFlow(flowId, {
            preview: false,
            active: true
        });

        flowStore.loadTransformedFlowResponse(response, false);

        if (asBothDraftAndActive) {
            flowStore.loadTransformedFlowResponse(response, true);
        }

        return response;
    }

    async fetchFlowToImport(flowId: string) {
        const { flowStore } = this.rootStore;
        const response = await this.fetchPlainFlow(flowId, {
            active: true
        });

        flowStore.loadTransformedFlowResponse(response, false);

        // I want to pass the theme even though it's an active flow.
        flowStore.flows[flowId]._theme = response.flow.theme;
        flowStore.flows[flowId].draftRevision =
            flowStore.flows[flowId].activeRevision;

        return response;
    }

    async createFlow() {
        const { flowService } = this.rootStore;

        const createdFlowResponse = await flowService.createFlow();
        const fetchedFlow = await flowService.fetchFlow(
            createdFlowResponse._id
        );

        this.updateFlowInStore(
            fetchedFlow.flow,
            fetchedFlow.pages,
            fetchedFlow.blocks,
            fetchedFlow.variables,
            true
        );
    }

    async createFlowTemplate(flowId: string, title: string) {
        const { flowService } = this.rootStore;
        return flowService.createFlowTemplate(flowId, title);
    }

    async duplicateFlowTemplate({
        flowId,
        title
    }: {
        flowId: string;
        title?: string;
    }) {
        const { flowService } = this.rootStore;
        return flowService.createFlowTemplate(flowId, title);
    }

    async deleteFlow(flowId: string) {
        const { flowService, toastStore } = this.rootStore;
        try {
            await flowService.deleteFlow(flowId);
            return true;
        } catch (e) {
            if (e.data.error_type === FlowApplyActionsErrors.cannotDeleteFlow) {
                toastStore.add({
                    text: e.data.error_message,
                    type: ToastType.Error
                });
                return false;
            }

            throw e;
        }
    }

    async getConnectedWorkflows(flowId: string) {
        const { flowService } = this.rootStore;
        return flowService.getConnectedWorkflows(flowId);
    }

    async getFlowRevisions(flowId: string) {
        const { flowService } = this.rootStore;
        const flowRevisions = await flowService.getFlowRevisions(flowId);

        return flowRevisions;
    }

    async getFlowByRevision(flowId: string, revisionId: string) {
        const { flowService } = this.rootStore;
        const { flow, blocks, pages, variables } =
            await flowService.getFlowByRevision(flowId, revisionId);

        const flowRevision = new FlowRevision(
            revisionId,
            flow._id,
            pages,
            blocks,
            variables,
            this.rootStore
        );

        return flowRevision;
    }

    async fetchLinkedAutomationsToFlow() {
        const {
            flowService,
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        const linkedAutomations = await flowService.getLinkedAutomationsPerFlow(
            selectedFlow._id
        );

        selectedFlow.setLinkedAutomations(linkedAutomations);
    }

    async updateFlow(
        id: string,
        data: PartialDeep<IFlow>,
        shouldUpdateLocally: boolean = true
    ) {
        const { flowService } = this.rootStore;

        try {
            const response = await flowService.updateFlow(id, data);
            if (shouldUpdateLocally) {
                this.updateFlowLocally(id, response);
            }

            return true;
        } catch (error) {
            trackError({ error });
            return false;
        }
    }

    async mergeFlowRevisions(id: string): Promise<ITransformedFlowResponse> {
        const { flowService } = this.rootStore;

        try {
            const response = await flowService.mergeFlowRevisions(id);
            this.updateFlowInStore(
                response.flow,
                response.pages,
                response.blocks,
                response.variables,
                true
            );

            return response;
        } catch (error) {
            trackError({ error });
            return null;
        }
    }

    async prePublish(id: string) {
        const { flowService } = this.rootStore;
        return flowService.prePublish(id);
    }

    async resetFlowSlugs(flowId: string): Promise<boolean> {
        const {
            flowService,
            builderUiStore: { setShowErrors }
        } = this.rootStore;

        try {
            await flowService.resetFlowSlugs(flowId);

            // this toggles the errorValidation for all blocks. after success we need to toggle off
            // so further edits won't trigger the errors:
            this.updateFlowLocally(flowId, {
                configuration: { flow_link_slug_reached_limit: false }
            });
            setShowErrors(false);

            // @ts-ignore
            return true;
        } catch (error) {
            trackError({ error });
            return false;
        }
    }

    async updateFlowConfiguration(
        flowId: string,
        flowConfiguration: TUpdateFlowConfiguration
    ): Promise<PartialDeep<IFlow>> {
        const {
            flowService,
            builderUiStore: { setShowErrors }
        } = this.rootStore;

        try {
            const response = await flowService.updateFlowConfiguration(
                flowId,
                flowConfiguration
            );

            this.updateFlowLocally(flowId, response);
            // this toggles the errorValidation for all blocks. after success we need to toggle off
            // so further edits won't trigger the errors:
            setShowErrors(false);

            // @ts-ignore
            return response;
        } catch (error) {
            trackError({ error });
            return {};
        }
    }

    async resetViewOnlyLink(flowId: string): Promise<PartialDeep<IFlow>> {
        const { flowService } = this.rootStore;

        try {
            const response = await flowService.resetViewOnlyLink(flowId);

            this.updateFlowLocally(flowId, response);
            return response;
        } catch (error) {
            trackError({ error });
            return {};
        }
    }

    async toggleViewOnlyLink(
        flowId: string,
        enabled: boolean
    ): Promise<PartialDeep<IFlow>> {
        const { flowService } = this.rootStore;

        try {
            const response = await flowService.toggleViewOnlyLink(
                flowId,
                enabled
            );

            this.updateFlowLocally(flowId, response);
            return response;
        } catch (error) {
            trackError({ error });
            return {};
        }
    }

    async publishFlow(flowId: string, reason?: PublishFlowReason) {
        const {
            flowStore,
            fullstoryService,
            actionsManager,
            globalContext: { clientPendingTaskCtrl }
        } = this.rootStore;

        const flow = flowStore.flows[flowId];

        try {
            flow.setIsBeingPublished(true);

            await actionsManager.requestsQueue.onPostRequestsExecution();

            await clientPendingTaskCtrl.performClientPendingTask({
                taskType: ClientPendingTaskType.PublishFlow,
                taskData: {
                    flow_id: flowId,
                    reason
                }
            });
            fullstoryService.debug(LogEvents.PublishedFlow(flowId));

            const isPublishSuccess = this.afterFlowPublished(flowId);
            flow.setIsBeingPublished(false);

            return await isPublishSuccess;
        } catch (error) {
            trackError({ error });
            return false;
        }
    }

    async shareFlow(
        flowId: string,
        recipientsIds: Array<string>,
        email: IVendorEmail
    ): Promise<{ not_sent: string[]; unsubscribed: string[] } | undefined> {
        const {
            flowStore,
            fullstoryService,
            globalContext: { clientPendingTaskCtrl }
        } = this.rootStore;
        const flow = flowStore.flows[flowId];

        // generate task data
        const taskData = this.getSendFlowTaskData(flowId, recipientsIds, email);
        taskData.email.html_body = toBinaryBase64(taskData.email.html_body);
        const isScheduledSend = !!taskData.scheduled_send_date;

        try {
            flow.setIsBeingPublished(true);
            const isPublishedBeforeShare = flow.is_published;

            const result = await clientPendingTaskCtrl.performClientPendingTask(
                {
                    taskType: ClientPendingTaskType.ShareFlow,
                    taskData
                }
            );

            const userIdsNotSent = Object.keys(result).filter(
                key => result[key].delivery.status === 'failure'
            );

            const userIdsUnsubscribed = Object.keys(result).filter(key => {
                return (
                    result[key].delivery.status === 'failure' &&
                    (result[key].delivery as IDeliveryFail).error?.includes(
                        'unsubscribed'
                    )
                );
            });

            if (userIdsNotSent?.length || userIdsUnsubscribed?.length) {
                return {
                    not_sent: userIdsNotSent,
                    unsubscribed: userIdsUnsubscribed
                };
            }

            // Finish task
            fullstoryService.debug(LogEvents.FlowShared);

            if (isScheduledSend) {
                fullstoryService.debug(
                    LogEvents.ScheduledSendEmailTime(
                        email.time_to_send.day,
                        email.time_to_send.hour
                    )
                );
                fullstoryService.debug(
                    LogEvents.TaskDataScheduledSendDate(
                        taskData.scheduled_send_date
                    )
                );
                flow.setScheduleSend(
                    new Date(
                        `${email.time_to_send.day} ${email.time_to_send.hour}`
                    ).toISOString()
                );
                this.updateFlow(flowId, {
                    share_flow_pending_task_data: email
                });
                fullstoryService.debug(LogEvents.SetScheduleSendSuccess);
            }

            // if not published yet, and not scheduled send (not publishing)
            // we should do after publish stuff because the flow was published during share
            if (!isPublishedBeforeShare && !isScheduledSend) {
                this.afterFlowPublished(flowId);
            }
        } catch (error) {
            trackError({ error });

            throw error;
        } finally {
            flow.setIsBeingPublished(false);
        }
    }

    async signContract(flow, variableId: string, data: Object) {
        const { flowService } = this.rootStore;

        const response = await flowService.signWithoutSubmission(flow._id, {
            variable_id: variableId,
            value: data
        });

        this.syncFlowRevisionsAfterActionOnActiveRevision(response, flow);
    }

    async discardDraft(id: string) {
        const { flowService } = this.rootStore;

        try {
            const response = await flowService.discardDraft(id);
            this.updateFlowInStore(
                response.flow,
                response.pages,
                response.blocks,
                response.variables,
                true
            );
            return true;
        } catch {
            return false;
        }
    }

    updateSignature({
        signeeId,
        block
    }: {
        signeeId: string;
        block: SignatureBlock;
    }) {
        const {
            builderUiStore: { selectedFlow, selectedWorkspace }
        } = this.rootStore;

        const { full_name = '', email = '' } =
            selectedFlow.getMemberById(signeeId) || {};

        const signeeType = selectedWorkspace.members.find(
            member => member._id === signeeId
        ).system_user_type;

        // Reset variable content if exists
        this.rootStore.variableCtrl.updateVariable(
            selectedFlow,
            block.connectedVariable,
            {
                signature: '',
                signed_on: null
            }
        );

        this.rootStore.blockCtrl.updateBlock(block, {
            signature: {
                signee_id: signeeId,
                signee_name: full_name,
                signee_email: email,
                signee_type: signeeType,
                signee_details: email
            }
        });
    }

    updateFlowLocally(flowId, data) {
        this.rootStore.flowStore.updateFlow(flowId, data);
    }

    async applyActions({
        flow,
        actions,
        retryNumber
    }: IApplyActionsProps): Promise<Array<any>> {
        if (!actions.length) {
            return;
        }

        const actionsResult = await this.fetchApplyActionsResponse({
            flow,
            actions,
            retryNumber
        });

        const actionIdToActionToApply = mapActionIdToAction(
            actions.map(({ _id }) => _id),
            this.rootStore.actionsManager.actionsStack.stack,
            actions
        );

        // sync flow with updates
        runInAction(() => {
            this.rootStore.flowStore.syncFlowWithServerResponse(
                flow,
                actionsResult.pages,
                actionsResult.blocks,
                actionsResult.variables,
                {
                    revision_id: actionsResult.revision_id,
                    revision_count: actionsResult.revision_count
                }
            );
        });

        actionsResult.actions.forEach(actionResult => {
            const action = actionIdToActionToApply[actionResult._id];

            action.onActionToServerResolved(actionResult, actionsResult);
        });

        if (actionsResult.last_content_edit) {
            flow.last_content_edit = actionsResult.last_content_edit;
        }

        actionsResult.actions.forEach(actionResult => {
            const action = actionIdToActionToApply[actionResult._id];

            action.setToSuccess();
        });

        flow.setLastActionNumber(actionsResult.last_action_number);
        if (actionsResult.require_strong_auth !== undefined) {
            flow.setRequireStrongAuth(actionsResult.require_strong_auth);
        }
    }

    private async fetchApplyActionsResponse({
        flow,
        actions,
        retryNumber
    }: IApplyActionsProps) {
        const { flowService } = this.rootStore;

        const actionsData = this.getActionsData(actions);

        if (retryNumber) {
            const recoveredResponse = await flowService.recoverFlowResponse(
                flow._id,
                actionsData
            );

            const lastActionNumberAtServer =
                recoveredResponse.last_action_number;
            const actionNumbers = actions.map(x => x.action_number);

            // If the last action number is lower then the min action number in the sent list it means the server didn't resolved the action
            if (
                min(actionNumbers) <= lastActionNumberAtServer &&
                max(actionNumbers) === lastActionNumberAtServer
            ) {
                return recoveredResponse;
            }
        }

        return flowService.applyActions(flow._id, actionsData);
    }

    private getActionsData(actions: Array<Action>) {
        let actionsData = actions.map(action => {
            const currActionData = {
                ...action.getDataForServer(),
                ...action.getBasicDataForServer() // Because we create a clone of the action we want to get the clone specific action data.
            };

            if (currActionData.description) {
                if (currActionData.redo) {
                    currActionData.description = `Redo: ${currActionData.description}`;
                }
                if (currActionData.undo) {
                    currActionData.description = `Undo: ${currActionData.description}`;
                }
            }

            return currActionData;
        });

        // Encode html content to prevent xss detection on cloudFlare
        return encodeHtmlOnApplyActionCommandContent(actionsData);
    }

    async reactivateFlow(
        flowId: string,
        flowConfiguration: PartialDeep<FlowConfigurationModel>
    ): Promise<PartialDeep<IFlow>> {
        return this.updateFlowConfiguration(flowId, flowConfiguration);
    }

    async sendTestFlowFromBuilder({
        emailContent,
        track
    }: {
        emailContent: IOneComposerEmail;
        track: TTrackFunction;
    }) {
        const {
            toastStore,
            fullstoryService,
            injectorService,
            sessionStore: { currUserId },
            builderUiStore: { selectedFlowId }
        } = this.rootStore;

        const subjectForMail = getSubjectForSendTestEmail(emailContent.subject);

        try {
            track(AnalyticsEvents.BuilderSendTestFlowViaEmail, {
                action: 'click'
            });

            const email = {
                ...emailContent,
                subject: convertSmartFieldsToServerFormat(subjectForMail),
                html_body: convertSmartFieldsToServerFormat(
                    emailContent.html_body
                )
            };

            const taskData = {
                is_preview: true,
                ...this.getSendFlowTaskData(selectedFlowId, [currUserId], email)
            };

            await injectorService
                .get('PendingTasksManager')
                .createPendingTask(ClientPendingTaskType.ShareFlow, taskData);

            fullstoryService.debug(LogEvents.FlowTestSent);

            track(AnalyticsEvents.BuilderSendTestFlowViaEmail, {
                action: 'success'
            });
        } catch (err) {
            track(AnalyticsEvents.BuilderSendTestFlowViaEmail, {
                action: 'error',
                error: err
            });
            toastStore.add({
                type: ToastType.Error,
                text: 'Some unexpected error has occurred, for assistance please contact our concierge service (concierge@honeybook.com)'
            });
            throw err;
        }
    }

    async sendTestFlowFromOnboarding(templateId: string) {
        const {
            flowService,
            sessionStore: { currUserId }
        } = this.rootStore;

        return flowService.sendTestFlowFromOnboarding(templateId, currUserId);
    }

    getSendFlowTaskData(
        flowId: string,
        recipientsIds: Array<string>,
        email: IVendorEmail
    ): IShareFlowTaskData {
        const {
            image_attachments,
            file_attachments,
            time_to_send,
            ...emailWithoutAttachments
        } = email;

        return {
            flow_id: flowId,
            recipients_ids: recipientsIds,
            email: emailWithoutAttachments,
            image_attachments,
            file_attachments,
            ...(time_to_send && {
                scheduled_send_date: new Date(
                    `${time_to_send.day} ${time_to_send.hour}`
                ).getTime(),
                scheduled_at: new Date()
            })
        };
    }

    setupBankAccount({
        institutionId,
        token
    }: {
        institutionId: string;
        token: string;
    }) {
        const { payService, userStore } = this.rootStore;

        const { currUser } = userStore;

        // flowViewUiStore.setIsPaymentInProgress(true);

        return payService.setupBankAccount({
            userId: currUser._id,
            institutionId,
            token
        });
    }

    updateFlowInStore(
        flow: IFlow,
        pages: Record<string, ITransformedPageResponse>,
        blocks: Record<string, TBlockResponse>,
        variables: Record<string, IVariableResponse>,
        isDraft: boolean
    ) {
        const { flowStore, builderUiStore, actionsManager } = this.rootStore;

        flowStore.load(flow, pages, blocks, variables, isDraft);

        // Select flow and first page
        builderUiStore.selectFlow(flow._id);
        actionsManager.setLastActionNumber(
            builderUiStore.selectedFlow.last_action_number,
            ActionManagerLastActionNumberReason.flowFromServer
        );

        if (!pages[builderUiStore.selectedPageId]) {
            const [{ page_id: selectedPageId }] = Object.values(pages).sort(
                (a, b) => a.order - b.order
            );
            builderUiStore.selectPage(selectedPageId);
            builderUiStore.refreshSidebar();
        }
        if (!blocks[builderUiStore.selectedBlockId]) {
            builderUiStore.selectBlock(null);
        }
    }

    selectBlock({
        blockId,
        displayRootParentBlock,
        isMultiStageAction
    }: ISelectBlockOptions = {}) {
        const { builderUiStore, eventEmitterCtrl } = this.rootStore;

        builderUiStore.selectBlock(blockId, displayRootParentBlock);
        builderUiStore.selectNavigationBlock(false);
        builderUiStore.setSelectedSmartFieldID(null);

        if (!blockId) {
            return;
        }

        eventEmitterCtrl.trigger(CoreOutboundEvents.BlockSelected, {
            blockId,
            isMultiStageFinished: !isMultiStageAction
        });
    }

    selectRootLevelParent(block?: TBlock) {
        const blockHorizontalParent = block?.calculateRootLevelParent();
        this.selectBlock({ blockId: blockHorizontalParent?.block_id });
    }

    resetSelectedBlock() {
        const { builderUiStore } = this.rootStore;

        builderUiStore.selectBlock(undefined);
        builderUiStore.selectNavigationBlock(false);
    }

    selectContractSmartField(fieldId: string, fieldType?: string) {
        const { builderUiStore } = this.rootStore;

        builderUiStore.setSelectedSmartFieldID(fieldId);
        let screen;
        switch (fieldType) {
            case 'payments_recap':
                screen = ContractSidebarScreen.PaymentsRecap;
                break;
            case 'services_recap':
                screen = ContractSidebarScreen.ServicesRecap;
                break;
            default:
                screen = ContractSidebarScreen.SmartField;
        }

        builderUiStore.setSelectedSidebarScreen(screen);
    }

    resetSelectedContractSmartField() {
        const { builderUiStore } = this.rootStore;

        builderUiStore.setSelectedSmartFieldID(null);
        builderUiStore.setSelectedSidebarScreen(
            ContractSidebarScreen.ContractContent
        );
    }

    reset() {
        const { builderUiStore } = this.rootStore;
        builderUiStore.reset();
    }

    selectNavigationBlock(select?: boolean) {
        const { builderUiStore, eventEmitterCtrl } = this.rootStore;

        builderUiStore.selectBlock();
        builderUiStore.selectNavigationBlock(select);
        builderUiStore.setShowPagesOverlayForId(null);

        eventEmitterCtrl.trigger(
            GeneralOutboundEvents.BlockSelectedNavigation,
            {}
        );
    }

    isSelectedBlock(block: TBlock) {
        const {
            builderUiStore: { selectedBlock }
        } = this.rootStore;

        return selectedBlock && block.block_id === selectedBlock.block_id;
    }

    configureFlowInNewTab(flowId) {
        goToFlow({
            flowId,
            openOnNewTab: true,
            forceRoute: FlowRouteType.edit,
            queryParams: { configure: 'true' }
        });
    }

    openNewDuplicate(flowId) {
        goToFlow({
            flowId,
            openOnNewTab: true,
            forceRoute: FlowRouteType.edit,
            queryParams: { new: 'true' }
        });
    }

    updateFlowOptimistically(
        flow: Flow,
        data: PartialDeep<Flow>,
        onAfterAction: () => void = null
    ) {
        this.addUpdateFlowAction(
            flow,
            data,
            ActionName.UpdateFlow,
            ServerActionType.UpdateFlow,
            onAfterAction
        );
    }

    updateFlowContent(
        flow: Flow,
        data: PartialDeep<Flow>,
        onAfterAction: () => void = null
    ) {
        this.addUpdateFlowAction(
            flow,
            data,
            ActionName.UpdateFlowContent,
            ServerActionType.UpdateFlowContent,
            onAfterAction
        );
    }

    showAuthenticationToast(
        type: Exclude<ToastType, ToastType.MemberToast>,
        action: () => void
    ) {
        const { toastStore } = this.rootStore;

        const toastText =
            type === ToastType.Info
                ? 'As client authentication is turned off for this file, clients will not be able to store payment details when paying this invoice, and use them later.'
                : 'Client authentication is turned off for this template, which significantly reduces the ability to verify your client’s identity, and may lead to unauthorized access to your files.';
        toastStore.add({
            type,
            text: toastText,
            iconColor: colors.neutral800,
            autoDismissMs: 0,
            actionLink: {
                text: 'CHANGE SECURITY SETTINGS',
                onClick: action,
                align: 'right'
            }
        });
    }

    private addUpdateFlowAction(
        flow: Flow,
        data: PartialDeep<Flow>,
        actionName: ActionName,
        serverActionType: ServerActionType,
        onAfterAction: () => void = null
    ) {
        const { actionsManager } = this.rootStore;

        const updateFlow = actionsManager.createAction<IUpdateFlowContext>(
            (newAction: Action<IUpdateFlowContext>) => ({
                name: actionName,
                serverActionType,
                ctx: {
                    flow,
                    data
                },
                createCommands: () => this.createUpdateFlowCommands(newAction),
                applySideEffects: () => onAfterAction && onAfterAction(),
                revertSideEffects: () => onAfterAction && onAfterAction(),
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );

        actionsManager.doNewApplyAction(updateFlow);
    }

    private createUpdateFlowCommands(action: Action<IUpdateFlowContext>) {
        const { flow, data } = action.ctx;

        const { configuration, ...restOfData } = data;

        const cleanFlow = toJS(flow);
        const oldData: Partial<Flow> = extractOldData(restOfData, cleanFlow);
        if (configuration) {
            oldData.configuration = extractOldData(
                configuration,
                cleanFlow.configuration
            );
        }

        action.setContext({
            commands: [createUpdateFlowCommand(flow._id, data)],
            // @ts-ignore
            revertCommands: [createUpdateFlowCommand(flow._id, oldData)]
        });
    }

    private getBaseDataForServer(action: Action<IUpdateFlowContext>) {
        const { commands } = action.ctx;

        return calculateFlowBaseDataForServer(commands, action);
    }

    private getRevertBaseDataForServer(action: Action<IUpdateFlowContext>) {
        const { revertCommands } = action.ctx;

        return calculateFlowBaseDataForServer(revertCommands, action);
    }

    syncFlowRevisionsAfterActionOnActiveRevision(
        response: IFlowUpdatedResponse,
        flow: Flow
    ) {
        const { flowStore } = this.rootStore;

        flowStore.syncFlowWithServerResponse(
            flow,
            response.pages,
            response.blocks,
            response.variables,
            {
                revisionType: RevisionType.Active
            }
        );

        if (flow.draftAndActiveAreEqual) {
            flowStore.syncFlowWithServerResponse(
                flow,
                response.pages,
                response.blocks,
                response.variables
            );
        }
    }

    expireFlow(flowId: string) {
        this.updateFlowConfiguration(flowId, {
            auto_expiration: { is_forced_expired: true }
        });
    }

    openExpirationPreviewInNewPage(flowId: string, expirationMessage: string) {
        const expirationPreviewUrl = getExpirationPreviewUrl(
            flowId,
            expirationMessage
        );

        window.open(`/app${expirationPreviewUrl}`, '_blank');
    }

    async starFlowTemplate() {
        const {
            flowService,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        selectedFlow.starred = !selectedFlow.starred;
        const flow = await flowService.updateFlow(selectedFlow._id, {
            starred: selectedFlow.starred
        });
        selectedFlow.setFlowStarred(flow.starred);
    }

    async moveFlowToFolder(folderId: string, flowId: string) {
        const {
            flowService,
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        const flow = await flowService.updateFlow(flowId, {
            folder_id: folderId
        });
        selectedFlow.updateFlowFolder(flow.folder_id, flow.path);
    }

    async markFlowAsCompleted(flowId: string) {
        const {
            flowService,
            builderUiStore: { selectedFlow }
        } = this.rootStore;

        const response = await flowService.markFlowAsCompleted(flowId);
        selectedFlow?.setStatus(response.status);
        return response;
    }

    private mergeFlowResponse(
        response: ITransformedFlowResponse,
        actionNumberBeforeFetch: number,
        active?: boolean,
        forceMerge?: boolean
    ) {
        const { builderUiStore, actionsManager, fullstoryService, flowCtrl } =
            this.rootStore;

        // If we don't have data locally just load or we want to force merge because of critical error.
        if (!builderUiStore.selectedFlow || forceMerge) {
            this.loadTransformedFlowResponse(response, active);

            return;
        }

        const actionNumberAfterFetch = actionsManager.lastActionNumber;
        const actionNumberFromServer = response.flow.last_action_number;

        // We have 3 action numbers, one before the fetch, one after the fetch that can be ahead because of local changes and the action number for the server state
        fullstoryService.debug(
            LogEvents.MergingFlowResponse(
                actionNumberBeforeFetch,
                actionNumberAfterFetch,
                actionNumberFromServer
            )
        );

        // We want to dismiss the load if the server and the data before the fetch are the same.
        if (actionNumberBeforeFetch === actionNumberFromServer) {
            if (actionNumberBeforeFetch === actionNumberAfterFetch) {
                fullstoryService.debug(LogEvents.NoChangesOnFlow);
            } else {
                fullstoryService.debug(LogEvents.LocalFlowIsAheadOfServer);
            }

            return;
        }

        // The server is behind the local state before the fetch Doesn't suppose to happen but we want to log if it would happen
        if (actionNumberBeforeFetch > actionNumberFromServer) {
            fullstoryService.warn(LogEvents.LocalResolvedFlowAheadOfServer);

            const action = actionsManager.findActionByNumber(
                actionNumberBeforeFetch
            );
            if (action && action.isLocalStatus) {
                fullstoryService.debug(LogEvents.LocalFlowIsAheadOfServer);

                return;
            }

            if (action && action.isResolvedStatus) {
                // we want to refetch to make sure that now we have the latest flow.
                flowCtrl.fetchFlow(builderUiStore.selectedFlowId, {
                    reason: FetchFlowReasons.conflictWhenActionsBeingResolved,
                    forceMerge: true
                });

                return;
            }
        }

        // If the server is ahead of the client that means that an action happened outside of the builder context and we want to reset the redo undo stack
        if (
            actionNumberBeforeFetch === actionNumberAfterFetch &&
            actionNumberAfterFetch < actionNumberFromServer
        ) {
            fullstoryService.warn(LogEvents.FlowInServerAheadOfLocal);

            actionsManager.resetRedoUndoStack();
        }

        // If the server and the local state are ahead of the before state so there is a conflict and we want to resolve to the server state.
        if (
            actionNumberBeforeFetch < actionNumberAfterFetch &&
            actionNumberBeforeFetch < actionNumberFromServer
        ) {
            fullstoryService.warn(LogEvents.FlowLocalConflictWithServer);

            // We don't want to await for this promise because it can cause an other fetch which will cause a loop. (the other fetch is for an edge case).
            this.handleFlowConflict(actionNumberBeforeFetch);
        }

        this.loadTransformedFlowResponse(response, active);
    }

    private async handleFlowConflict(actionNumberBeforeFetch: number) {
        const { actionsManager } = this.rootStore;

        await actionsManager.handleConflict(actionNumberBeforeFetch + 1);
    }

    private showMarkAsPaidError(errorMsg) {
        this.rootStore.toastStore.add({
            title: 'Something went wrong',
            text: errorMsg,
            type: ToastType.Error,
            autoDismissMs: 12000
        });
        return null;
    }

    async onFlowUpdate(
        type: FlowInformActionTypes,
        data: any,
        recreateBlocks?: boolean
    ) {
        const {
            builderUiStore: { selectedFlowId },
            fullstoryService,
            flowStore,
            companyStore,
            configStore: { sessionId }
        } = this.rootStore;
        switch (type) {
            case FlowInformActionTypes.reload: {
                const { sessionId: updateFromSessionId } =
                    data as IReloadAction;

                if (sessionId === updateFromSessionId) {
                    return;
                }

                await this.fetchFlow(selectedFlowId, {
                    reason: FetchFlowReasons.webSocketReloadRequest
                });

                break;
            }
            case FlowInformActionTypes.update: {
                const {
                    flow: flowUpdateData,
                    variables,
                    blocks,
                    pages
                } = transformFlowSocketUpdateData(
                    data,
                    companyStore.currCompany?.default_timezone
                );

                delete flowUpdateData.variables;

                const flow = flowStore.flows[flowUpdateData._id];

                flowStore.updateFlow(
                    flowUpdateData._id,
                    new Flow(flowUpdateData, this.rootStore)
                );
                flowStore.syncFlowWithServerResponse(
                    flow,
                    Object.values(pages),
                    Object.values(blocks),
                    Object.values(variables),
                    { revisionType: RevisionType.Draft, recreateBlocks }
                );

                if (flow.activeRevision) {
                    flowStore.syncFlowWithServerResponse(
                        flow,
                        Object.values(pages),
                        Object.values(blocks),
                        Object.values(variables),
                        { revisionType: RevisionType.Active, recreateBlocks }
                    );
                }

                break;
            }
            default: {
                fullstoryService.error(LogEvents.SocketEventHasNoHandler(type));
                break;
            }
        }
    }

    isFlowLoadedWithDraft(flowToImportFromId: string) {
        const { flowStore } = this.rootStore;
        const flow = flowStore.flows[flowToImportFromId];

        return flow && flow.draftRevision;
    }

    async afterFlowPublished(flowId: string) {
        const {
            flowStore,
            builderUiStore: { setShowErrors }
        } = this.rootStore;

        const flow = flowStore.flows[flowId];

        try {
            const response = await this.fetchPlainFlow(flowId);

            runInAction(() => {
                flowStore.loadTransformedFlowResponse(response, true);

                flow.setActiveRevision(flow.draftRevision.clone(flow._id));
                flow.setDraftRevision(flow.draftRevision.clone(flow._id));
            });

            flow.setIsBeingPublished(false);

            flow.setDirty(false);
            setShowErrors(false);
            return true;
        } catch (error) {
            trackError({ error });
            return false;
        }
    }

    isCustomFontsInFlow(selectedflowTheme: IFlowTheme) {
        const { companyStore } = this.rootStore;

        return companyStore.currCompany.company_fonts?.some(font => {
            return (
                selectedflowTheme?.buttons_styles?.font_family === font.name ||
                selectedflowTheme?.questions_styles?.font_family ===
                    font.name ||
                selectedflowTheme?.styles?.font_family === font.name
            );
        });
    }

    // #region LeadForm
    async createPublicFlow(flowId?: string, flowSource?: TFlowSource) {
        const {
            flowService,
            fullstoryService,
            templatesUiStore: { addPublicFlowTemplate }
        } = this.rootStore;

        try {
            const newPublicFlow = await flowService.createPublicFlow({
                flowId,
                flowSource
            });
            addPublicFlowTemplate(newPublicFlow);
            return newPublicFlow;
        } catch (error) {
            fullstoryService.error(error);

            this.rootStore.toastStore.add({
                text: this.leadFormT('create_error'),
                type: ToastType.Error
            });
            throw error;
        }
    }

    async duplicatePublicFlow({
        flowId,
        flowSource
    }: {
        flowId: string;
        flowSource?: TFlowSource;
    }) {
        return this.createPublicFlow(flowId, flowSource);
    }

    async deletePublicFlow(flowId: string) {
        const {
            flowService,
            fullstoryService,
            templatesUiStore: { deletePublicFlow }
        } = this.rootStore;
        let res: boolean;
        try {
            await flowService.deletePublicFlow(flowId);
            deletePublicFlow(flowId);
            res = true;
        } catch (error) {
            res = false;
            const errorMessage = this.leadFormT('delete_error');
            fullstoryService.error(error);
            this.rootStore.toastStore.add({
                text: errorMessage,
                type: ToastType.Error
            });
            throw error;
        } finally {
            return res;
        }
    }

    async getEmbeddedScript(flowId: string) {
        const { flowService, fullstoryService } = this.rootStore;
        try {
            const res = await flowService.getEmbeddedFormScript(flowId);
            return res;
        } catch (error) {
            fullstoryService.error(error);
            throw error;
        }
    }

    async sharePublicFlow({ flowId }: { flowId: string }) {
        const taskData = { flow_id: flowId, user_agent: navigator.userAgent };
        try {
            const {
                builderUiStore: { selectedFlow },
                globalContext: { clientPendingTaskCtrl }
            } = this.rootStore;

            if (!selectedFlow.hasLeadDetailsBlock) {
                trackError({
                    error: new Error(
                        'Failed to publish form without lead details block'
                    )
                });
                this.rootStore.toastStore.add({
                    text: this.leadFormT('lead_details_block_error'),
                    type: ToastType.Error
                });
                return { flow_url: undefined };
            }

            const result: { flow_url: string } =
                await clientPendingTaskCtrl.performClientPendingTask({
                    taskType: ClientPendingTaskType.ShareLeadForm,
                    taskData
                });

            const isSuccess = await this.afterFlowPublished(flowId);
            if (isSuccess) {
                this.addPublishedStateToUrl();
            }
            return result;
        } catch (error) {
            trackError({ error });
            this.rootStore.toastStore.add({
                text: this.leadFormT('error_toast'),
                type: ToastType.Error
            });
            return { flow_url: undefined };
        }
    }

    addPublishedStateToUrl() {
        const newUrl = addParamsToUrl({
            params: { published: 'true' },
            url: window.location.href
        });
        window.history.replaceState('', '', newUrl);
    }

    @mobxAction.bound
    async togglePublicFlowActivation(flowId: string, isActive: boolean) {
        // this function only handles the flag and does NOT publish/ update the flow
        const { flowService, toastStore } = this.rootStore;

        let response;
        try {
            if (isActive) {
                response = await flowService.activatePublicFlow(flowId);
            } else {
                response = await flowService.deactivatePublicFlow(flowId);
            }

            this.updateFlowLocally(flowId, {
                deactivated_public_flow: !isActive
            });

            return response;
        } catch (error) {
            trackError({ error });
            toastStore.add({
                type: ToastType.Error,
                text: 'Something went wrong'
            });
            throw new Error(error);
        }
    }

    async createAndGoToPublicFlow({
        templateId,
        track,
        eventSource,
        flowService,
        openOnNewTab,
        history
    }: {
        templateId?: string;
        track: TTrackFunction;
        eventSource: string;
        flowService: IFlowService;
        openOnNewTab?: boolean;
        history?: ReturnType<typeof useHistory>;
    }) {
        track(AnalyticsEvents.NewPublicFlow);
        const newPublicFlow = await this.createPublicFlow(templateId);
        track(AnalyticsEvents.GoToFlow, {
            source: eventSource,
            type: CreateTemplateType.Lead_form,
            template_id: newPublicFlow._id
        });
        goToFlow({
            flowId: newPublicFlow._id,
            FlowService: flowService,
            useAngularNavigation: !isReactFirst(),
            openOnNewTab: openOnNewTab,
            history
        });
    }
    // #endregion
}
